import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import NetworkStats from './components/NetworkStats';
import MapFilters from './components/MapFilters';
import InteractiveMap from './components/InteractiveMap';
import PartnershipCard from './components/PartnershipCard';
import PartnershipTimeline from './components/PartnershipTimeline';
import SuccessMetrics from './components/SuccessMetrics';
import PartnershipInquiry from './components/PartnershipInquiry';

const PartnershipNetwork = () => {
  const { t } = useTranslation();
  const [activeFilters, setActiveFilters] = useState({
    league: 'all',
    tier: 'all',
    type: 'all',
    country: 'all'
  });

  const [filteredPartnerships, setFilteredPartnerships] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const networkStats = [
  {
    id: 1,
    label: 'Total Partnerships',
    value: '38',
    icon: 'Handshake',
    bgColor: 'bg-primary/10',
    iconColor: 'var(--color-primary)',
    change: 15
  },
  {
    id: 2,
    label: 'Active Transfers',
    value: '127',
    icon: 'TrendingUp',
    bgColor: 'bg-success/10',
    iconColor: 'var(--color-success)',
    change: 23
  },
  {
    id: 3,
    label: 'Countries Covered',
    value: '12',
    icon: 'Globe',
    bgColor: 'bg-accent/10',
    iconColor: 'var(--color-accent)',
    change: 8
  },
  {
    id: 4,
    label: 'Success Rate',
    value: '94%',
    icon: 'Award',
    bgColor: 'bg-secondary/10',
    iconColor: 'var(--color-secondary)',
    change: 5
  }];


  const partnerships = [
  {
    id: 1,
    name: 'Manchester United FC',
    league: 'Premier League',
    location: 'Manchester, England',
    since: 2019,
    tier: 'platinum',
    transfers: 12,
    placements: 8,
    active: 3,
    specialties: ['Strikers', 'Midfielders', 'Youth Development'],
    image: "https://images.unsplash.com/photo-1626078859283-7e83d7e4137f",
    imageAlt: 'Modern football stadium with red seats filled with passionate fans during evening match under bright floodlights',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1403ca550-1769504009201.png",
    logoAlt: 'Professional football club emblem with red and white colors featuring iconic devil mascot'
  },
  {
    id: 2,
    name: 'Real Madrid CF',
    league: 'La Liga',
    location: 'Madrid, Spain',
    since: 2020,
    tier: 'platinum',
    transfers: 10,
    placements: 7,
    active: 2,
    specialties: ['Wingers', 'Defenders', 'Technical Skills'],
    image: "https://images.unsplash.com/photo-1724500760032-b2eb510e59c4",
    imageAlt: 'Iconic white football stadium with distinctive architecture and pristine green pitch under clear blue sky',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_19cbaf864-1769504007725.png",
    logoAlt: 'Royal football club crest with golden crown and white background representing Spanish football excellence'
  },
  {
    id: 3,
    name: 'AC Milan',
    league: 'Serie A',
    location: 'Milan, Italy',
    since: 2021,
    tier: 'gold',
    transfers: 8,
    placements: 6,
    active: 2,
    specialties: ['Defenders', 'Goalkeepers', 'Tactical Training'],
    image: "https://images.unsplash.com/photo-1676474330917-1440a1836174",
    imageAlt: 'Historic Italian football stadium with red and black team colors and passionate supporters in stands',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_12d3d90d7-1769504009208.png",
    logoAlt: 'Classic Italian football club badge with red and black stripes and devil emblem'
  },
  {
    id: 4,
    name: 'Bayern Munich',
    league: 'Bundesliga',
    location: 'Munich, Germany',
    since: 2020,
    tier: 'platinum',
    transfers: 11,
    placements: 9,
    active: 4,
    specialties: ['All Positions', 'Youth Academy', 'Professional Development'],
    image: "https://images.unsplash.com/photo-1691926384382-95c46b9816de",
    imageAlt: 'Modern German football arena with red seats and state-of-the-art facilities during championship match',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_130e6d255-1769504010112.png",
    logoAlt: 'German football club logo with red and white colors featuring traditional Bavarian design elements'
  },
  {
    id: 5,
    name: 'Paris Saint-Germain',
    league: 'Ligue 1',
    location: 'Paris, France',
    since: 2022,
    tier: 'gold',
    transfers: 7,
    placements: 5,
    active: 2,
    specialties: ['Attackers', 'Creative Midfielders', 'Star Development'],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f8ba9c06-1770710524265.png",
    imageAlt: 'Prestigious French football stadium with blue and red colors and Eiffel Tower visible in background',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_11490d1e9-1771943344369.png",
    logoAlt: 'French football club emblem with blue background and red accents featuring Parisian tower symbol'
  },
  {
    id: 6,
    name: 'Liverpool FC',
    league: 'Premier League',
    location: 'Liverpool, England',
    since: 2019,
    tier: 'gold',
    transfers: 9,
    placements: 7,
    active: 3,
    specialties: ['Full Backs', 'Pressing Game', 'High Intensity'],
    image: "https://images.unsplash.com/photo-1674575017131-c2df75e0877d",
    imageAlt: 'Historic English football ground with red seats and famous Kop stand filled with singing supporters',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1d27fbd1c-1771943348508.png",
    logoAlt: 'English football club badge with red liver bird symbol and traditional shield design'
  }];


  const milestones = [
  {
    id: 1,
    title: 'Partnership Network Launch',
    date: 'January 2019',
    description: 'Established first strategic partnerships with three Premier League clubs, marking the beginning of our European expansion.',
    icon: 'Rocket',
    metrics: [
    { icon: 'Users', label: 'Initial Partners', value: '3' },
    { icon: 'MapPin', label: 'Countries', value: '1' }]

  },
  {
    id: 2,
    title: 'La Liga Expansion',
    date: 'June 2020',
    description: 'Successfully expanded into Spanish football market with partnerships across La Liga clubs, opening new opportunities for African talent.',
    icon: 'Globe',
    metrics: [
    { icon: 'Trophy', label: 'New Partnerships', value: '5' },
    { icon: 'TrendingUp', label: 'Transfers', value: '12' }]

  },
  {
    id: 3,
    title: 'Serie A & Bundesliga Entry',
    date: 'March 2021',
    description: 'Established strong presence in Italian and German football markets, diversifying our network across major European leagues.',
    icon: 'Target',
    metrics: [
    { icon: 'Building', label: 'Clubs Added', value: '8' },
    { icon: 'Users', label: 'Players Placed', value: '24' }]

  },
  {
    id: 4,
    title: 'Platinum Partnership Tier',
    date: 'September 2022',
    description: 'Introduced platinum partnership tier for elite clubs with exclusive access to premium talent and enhanced collaboration benefits.',
    icon: 'Crown',
    metrics: [
    { icon: 'Award', label: 'Platinum Partners', value: '4' },
    { icon: 'DollarSign', label: 'Transfer Value', value: '€45M' }]

  },
  {
    id: 5,
    title: 'Academy Partnership Program',
    date: 'February 2023',
    description: 'Launched specialized academy partnership program focusing on youth development and long-term talent pipeline creation.',
    icon: 'GraduationCap',
    metrics: [
    { icon: 'School', label: 'Academies', value: '12' },
    { icon: 'Users', label: 'Youth Players', value: '67' }]

  },
  {
    id: 6,
    title: 'Network Milestone Achievement',
    date: 'November 2025',
    description: 'Reached 38 active partnerships across 12 European countries with 94% success rate in player placements and transfers.',
    icon: 'Star',
    metrics: [
    { icon: 'Handshake', label: 'Total Partners', value: '38' },
    { icon: 'CheckCircle', label: 'Success Rate', value: '94%' }]

  }];


  const successMetrics = [
  {
    id: 1,
    title: 'Transfer Success Rate',
    category: 'Performance',
    value: '94%',
    description: 'Successful player placements and transfers',
    icon: 'Target',
    bgColor: 'bg-success/10',
    iconColor: 'var(--color-success)',
    trend: 5,
    breakdown: [
    { label: 'Completed Transfers', value: '127' },
    { label: 'Active Negotiations', value: '23' },
    { label: 'Failed Attempts', value: '8' }]

  },
  {
    id: 2,
    title: 'Total Transfer Value',
    category: 'Financial',
    value: '€156M',
    description: 'Combined value of all successful transfers',
    icon: 'DollarSign',
    bgColor: 'bg-primary/10',
    iconColor: 'var(--color-primary)',
    trend: 18,
    breakdown: [
    { label: 'Average Transfer', value: '€1.2M' },
    { label: 'Highest Transfer', value: '€8.5M' },
    { label: 'Commission Earned', value: '€7.8M' }]

  },
  {
    id: 3,
    title: 'Player Satisfaction',
    category: 'Quality',
    value: '4.8/5.0',
    description: 'Average rating from placed players',
    icon: 'Heart',
    bgColor: 'bg-accent/10',
    iconColor: 'var(--color-accent)',
    trend: 3,
    breakdown: [
    { label: 'Career Development', value: '4.9/5' },
    { label: 'Support Services', value: '4.7/5' },
    { label: 'Communication', value: '4.8/5' }]

  },
  {
    id: 4,
    title: 'Partnership Retention',
    category: 'Relationship',
    value: '96%',
    description: 'Clubs continuing partnership annually',
    icon: 'Repeat',
    bgColor: 'bg-secondary/10',
    iconColor: 'var(--color-secondary)',
    trend: 2,
    breakdown: [
    { label: 'Renewed Partnerships', value: '36' },
    { label: 'New Partnerships', value: '8' },
    { label: 'Ended Partnerships', value: '2' }]

  }];


  const handleFilterChange = (category, value) => {
    setActiveFilters((prev) => ({ ...prev, [category]: value }));
  };

  const handleClearFilters = () => {
    setActiveFilters({
      league: 'all',
      tier: 'all',
      type: 'all',
      country: 'all'
    });
  };

  const handlePartnershipClick = (regionId) => {
    setSelectedRegion(regionId);
    const element = document.getElementById('partnerships-grid');
    if (element) {
      element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleViewDetails = (partnershipId) => {
    alert(`Viewing details for partnership ID: ${partnershipId}\n\nThis would open a detailed partnership profile with:\n• Full collaboration history\n• Transfer statistics\n• Contact information\n• Success stories\n• Active opportunities`);
  };

  useEffect(() => {
    let filtered = [...partnerships];

    if (activeFilters?.league !== 'all') {
      const leagueMap = {
        'premier-league': 'Premier League',
        'la-liga': 'La Liga',
        'serie-a': 'Serie A',
        'bundesliga': 'Bundesliga',
        'ligue-1': 'Ligue 1'
      };
      filtered = filtered?.filter((p) => p?.league === leagueMap?.[activeFilters?.league]);
    }

    if (activeFilters?.tier !== 'all') {
      filtered = filtered?.filter((p) => p?.tier === activeFilters?.tier);
    }

    if (activeFilters?.country !== 'all') {
      const countryMap = {
        'england': 'England',
        'spain': 'Spain',
        'italy': 'Italy',
        'germany': 'Germany',
        'france': 'France'
      };
      filtered = filtered?.filter((p) => p?.location?.includes(countryMap?.[activeFilters?.country]));
    }

    setFilteredPartnerships(filtered);
  }, [activeFilters]);

  return (
    <>
      <Helmet>
        <title>Partnership Network - ProX11Market | Global Football Club Relationships</title>
        <meta
          name="description"
          content="Explore ProX11Market's extensive network of 38+ European football partnerships across Premier League, La Liga, Serie A, Bundesliga, and Ligue 1. View success metrics, transfer statistics, and collaboration opportunities." />

      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-20 pb-12 md:pb-16 lg:pb-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 space-y-8 md:space-y-12">
            <div className="text-center space-y-4">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full">
                <Icon name="Globe" size={16} color="var(--color-primary)" />
                <span className="text-sm font-semibold text-primary">Global Network</span>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground font-headline">
                Partnership Network
              </h1>
              <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
                Connecting African football excellence with European opportunities through strategic partnerships across 12 countries and 38+ elite clubs
              </p>
            </div>

            <NetworkStats stats={networkStats} />

            <MapFilters
              activeFilters={activeFilters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters} />


            <InteractiveMap
              partnerships={partnerships}
              onPartnershipClick={handlePartnershipClick} />


            <div id="partnerships-grid" className="space-y-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground font-headline">
                    Our Partnerships
                  </h2>
                  <p className="text-sm md:text-base text-muted-foreground mt-1">
                    {filteredPartnerships?.length} partnerships found
                  </p>
                </div>
                <Button
                  variant="outline"
                  iconName="Download"
                  iconPosition="left"
                  className="w-full md:w-auto">

                  Download Network Report
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {filteredPartnerships?.map((partnership) =>
                <PartnershipCard
                  key={partnership?.id}
                  partnership={partnership}
                  onViewDetails={handleViewDetails} />

                )}
              </div>

              {filteredPartnerships?.length === 0 &&
              <div className="text-center py-12 md:py-16">
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Search" size={32} color="var(--color-muted-foreground)" />
                  </div>
                  <h3 className="text-lg md:text-xl font-semibold text-foreground mb-2">
                    No partnerships found
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground mb-6">
                    Try adjusting your filters to see more results
                  </p>
                  <Button variant="outline" onClick={handleClearFilters}>
                    Clear All Filters
                  </Button>
                </div>
              }
            </div>

            <SuccessMetrics metrics={successMetrics} />

            <PartnershipTimeline milestones={milestones} />

            <PartnershipInquiry />

            <div className="bg-gradient-to-br from-primary to-primary/80 rounded-lg p-6 md:p-8 lg:p-12 text-center">
              <div className="max-w-3xl mx-auto space-y-6">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto">
                  <Icon name="Users" size={32} color="#FFFFFF" />
                </div>
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white font-headline">
                  Ready to Join Our Network?
                </h2>
                <p className="text-base md:text-lg text-white/90">
                  Partner with ProX11Market to access premium African football talent and expand your club's global reach through our proven transfer facilitation services
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    variant="secondary"
                    size="lg"
                    iconName="Calendar"
                    iconPosition="right"
                    onClick={() => window.location.href = '/contact'}>

                    Schedule Partnership Discussion
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    iconName="FileText"
                    iconPosition="left"
                    className="bg-white/10 border-white/30 text-white hover:bg-white/20">

                    Download Partnership Brochure
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </main>

        <footer className="bg-card border-t border-border py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Trophy" size={20} color="var(--color-primary)" />
                  </div>
                  <span className="text-lg font-bold text-primary font-headline">ProX11Market</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Connecting African football excellence with European opportunities through strategic partnerships and FIFA-compliant processes.
                </p>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-foreground mb-4">Quick Links</h3>
                <ul className="space-y-2">
                  <li>
                    <a href="/player-profiles" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                      Player Profiles
                    </a>
                  </li>
                  <li>
                    <a href="/services-overview" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                      Services
                    </a>
                  </li>
                  <li>
                    <a href="/success-stories" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                      Success Stories
                    </a>
                  </li>
                  <li>
                    <a href="/market-intelligence" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                      Market Intelligence
                    </a>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-foreground mb-4">Legal</h3>
                <ul className="space-y-2">
                  <li>
                    <a href="/compliance-center" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                      Compliance Center
                    </a>
                  </li>
                  <li>
                    <a href="#" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                      Privacy Policy
                    </a>
                  </li>
                  <li>
                    <a href="#" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                      Terms of Service
                    </a>
                  </li>
                  <li>
                    <a href="#" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                      Cookie Policy
                    </a>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-foreground mb-4">Contact</h3>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="Mail" size={14} />
                    partnerships@prox11market.com
                  </li>
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="Phone" size={14} />
                    +44 20 1234 5678
                  </li>
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="MapPin" size={14} />
                    London, United Kingdom
                  </li>
                </ul>
              </div>
            </div>

            <div className="pt-8 border-t border-border flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-sm text-muted-foreground">
                &copy; {new Date()?.getFullYear()} ProX11Market. All rights reserved.
              </p>
              <div className="flex items-center gap-4">
                <a href="#" className="text-muted-foreground hover:text-primary transition-colors">
                  <Icon name="Linkedin" size={20} />
                </a>
                <a href="#" className="text-muted-foreground hover:text-primary transition-colors">
                  <Icon name="Twitter" size={20} />
                </a>
                <a href="#" className="text-muted-foreground hover:text-primary transition-colors">
                  <Icon name="Facebook" size={20} />
                </a>
                <a href="#" className="text-muted-foreground hover:text-primary transition-colors">
                  <Icon name="Instagram" size={20} />
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>);

};

export default PartnershipNetwork;